import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, MessageCircle, Pin, Eye, EyeOff, Trash2, X, PenLine, ShieldCheck } from "lucide-react";
import {
  getCategories, getPosts, createPost, likePost,
  pinPost, hidePost, deletePost, apiError,
} from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import HumanCheck from "@/components/HumanCheck";
import Newsletter from "@/components/Newsletter";

const likedKey = "arw_liked_posts";
const getLiked = () => JSON.parse(localStorage.getItem(likedKey) || "[]");
const addLiked = (id) => localStorage.setItem(likedKey, JSON.stringify([...getLiked(), id]));

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Community() {
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("");
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [cats, ps] = await Promise.all([getCategories(), getPosts(active || undefined)]);
    setCategories(cats);
    setPosts(ps);
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const onLike = async (id) => {
    if (getLiked().includes(id)) return;
    try {
      const r = await likePost(id);
      addLiked(id);
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, likes: r.likes } : p)));
    } catch { /* noop */ }
  };

  const modAction = async (fn, id, msg) => {
    try {
      await fn(id);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="community-page" className="pt-32">
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs tracking-accent uppercase text-amber-500 mb-4">The Reader Community</p>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tight">The Gathering</h1>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Share theories, ask me anything, and talk with fellow readers of The Luminaria Chronicles. Post with just your name and email. No account needed.
            </p>
          </div>
          <button data-testid="open-new-post" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-6 py-4 hover:bg-amber-400 transition-colors whitespace-nowrap">
            <PenLine size={16} /> Start a discussion
          </button>
        </motion.div>
      </section>

      {/* category tabs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        <div className="flex flex-wrap gap-3">
          <button data-testid="cat-all" onClick={() => setActive("")} className={`px-4 py-2 text-xs tracking-accent uppercase border transition-colors ${active === "" ? "bg-amber-500 text-zinc-950 border-amber-500" : "border-zinc-700 text-zinc-400 hover:border-amber-500"}`}>
            All
          </button>
          {categories.map((c) => (
            <button key={c.slug} data-testid={`cat-${c.slug}`} onClick={() => setActive(c.slug)} className={`px-4 py-2 text-xs tracking-accent uppercase border transition-colors ${active === c.slug ? "bg-amber-500 text-zinc-950 border-amber-500" : "border-zinc-700 text-zinc-400 hover:border-amber-500"}`}>
            {c.name} <span className="opacity-60">({c.post_count})</span>
            </button>
          ))}
        </div>
      </section>

      {/* posts */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        {posts.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-12 text-center" data-testid="community-empty">
            <p className="text-zinc-400">No discussions yet. Be the first to start one!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                data-testid={`post-card-${p.id}`}
                onClick={() => navigate(`/community/${p.id}`)}
                className={`border p-6 transition-colors cursor-pointer ${p.hidden ? "border-red-900/50 bg-red-950/10" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"}`}
              >
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {p.pinned && <span className="inline-flex items-center gap-1 text-[10px] tracking-accent uppercase text-amber-500"><Pin size={12} /> Pinned</span>}
                  <span className="text-[10px] tracking-accent uppercase text-zinc-500 border border-zinc-700 px-2 py-0.5">{catName(p.category)}</span>
                  {p.hidden && <span className="text-[10px] tracking-accent uppercase text-red-400">Hidden</span>}
                </div>
                <Link to={`/community/${p.id}`} className="group">
                  <h3 className="font-display text-2xl font-semibold group-hover:text-amber-500 transition-colors">{p.title}</h3>
                </Link>
                <p className="mt-2 text-zinc-400 line-clamp-2 leading-relaxed">{p.body}</p>
                <div className="mt-4 flex items-center gap-5 text-sm text-zinc-500">
                  <span className="flex items-center gap-1.5">
                    {p.is_author ? <span className="text-amber-500 inline-flex items-center gap-1"><ShieldCheck size={14} /> {p.author_name}</span> : p.author_name}
                  </span>
                  <span>{timeAgo(p.created_at)}</span>
                  <button data-testid={`like-post-${p.id}`} onClick={(e) => { e.stopPropagation(); onLike(p.id); }} className={`flex items-center gap-1.5 transition-colors ${getLiked().includes(p.id) ? "text-amber-500" : "hover:text-amber-500"}`}>
                    <Heart size={15} fill={getLiked().includes(p.id) ? "currentColor" : "none"} /> {p.likes}
                  </button>
                  <Link to={`/community/${p.id}`} className="flex items-center gap-1.5 hover:text-amber-500 transition-colors">
                    <MessageCircle size={15} /> {p.reply_count}
                  </Link>
                </div>
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-zinc-800 flex gap-4 text-xs">
                    <button data-testid={`pin-${p.id}`} onClick={(e) => { e.stopPropagation(); modAction(pinPost, p.id, p.pinned ? "Unpinned" : "Pinned"); }} className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500"><Pin size={13} /> {p.pinned ? "Unpin" : "Pin"}</button>
                    <button data-testid={`hide-${p.id}`} onClick={(e) => { e.stopPropagation(); modAction(hidePost, p.id, p.hidden ? "Shown" : "Hidden"); }} className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500">{p.hidden ? <><Eye size={13} /> Show</> : <><EyeOff size={13} /> Hide</>}</button>
                    <button data-testid={`delete-${p.id}`} onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this post and all replies?")) modAction(deletePost, p.id, "Deleted"); }} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 size={13} /> Delete</button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <Newsletter />

      {open && (
        <NewPostModal categories={categories} defaultCat={active} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
      )}
    </div>
  );
}

function NewPostModal({ categories, defaultCat, onClose, onCreated }) {
  const [form, setForm] = useState({
    category: defaultCat || (categories[0]?.slug ?? "general-discussion"),
    name: "", email: "", title: "", body: "", join_list: true,
  });
  const [human, setHuman] = useState({ challenge_id: "", challenge_answer: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createPost({ ...form, ...human });
      toast.success("Posted! Thanks for joining the conversation.");
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
      setRefreshKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-zinc-950/80 backdrop-blur-sm p-4 py-16" data-testid="new-post-modal">
      <div className="relative w-full max-w-xl border border-zinc-800 bg-zinc-900 p-8">
        <button onClick={onClose} data-testid="close-new-post" className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200"><X size={20} /></button>
        <p className="text-xs tracking-accent uppercase text-amber-500 mb-2">New discussion</p>
        <h2 className="font-display text-2xl font-bold mb-6">Share your thoughts</h2>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs tracking-accent uppercase text-zinc-500 mb-2">Category</label>
            <select data-testid="new-post-category" value={form.category} onChange={up("category")} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="new-post-name" required placeholder="Your name" value={form.name} onChange={up("name")} className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
            <input data-testid="new-post-email" required type="email" placeholder="your@email.com" value={form.email} onChange={up("email")} className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          </div>
          <input data-testid="new-post-title" required placeholder="Discussion title" value={form.title} onChange={up("title")} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          <textarea data-testid="new-post-body" required rows={5} placeholder="What's on your mind?" value={form.body} onChange={up("body")} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input data-testid="new-post-joinlist" type="checkbox" checked={form.join_list} onChange={(e) => setForm((f) => ({ ...f, join_list: e.target.checked }))} className="accent-amber-500" />
            Add me to the reader list for updates
          </label>
          <HumanCheck value={human} onChange={setHuman} refreshKey={refreshKey} />
          <button data-testid="submit-new-post" type="submit" disabled={loading} className="w-full bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-6 py-4 hover:bg-amber-400 transition-colors disabled:opacity-60">
            {loading ? "Posting…" : "Post discussion"}
          </button>
        </form>
      </div>
    </div>
  );
}
